import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import {
  Shield,
  Users,
  Building2,
  Activity,
  AlertCircle,
  AlertTriangle,
  Eye,
} from 'lucide-react'
import { AdminNavigation } from '../components/Navigation'
import { EmptyState } from '../components/EmptyState'
import { StatusBadge } from '../components/StatusBadge'
import { TenantLifecycleControls } from '../components/TenantLifecycleControls'
import { AccountsPanel } from '../components/AccountsPanel'
import { CanariesPanel } from '../components/CanariesPanel'
import { useAuthStore } from '../stores/authStore'
import { cn, timeAgo } from '../lib/utils'
import {
  getAdminDashboard,
  getAuditLog,
  getPrincipals,
  getTenants,
  type AdminDashboardStats,
} from '../lib/api'
import type { AuditLogEntry, Principal, Tenant } from '../types'

// §7 honest alerting: every number and row on this page is fetched from the
// backend or honestly absent. The old version rendered fabricated
// principals/tenants/audit entries, invented stats, and a FAKE kill switch
// (a setTimeout pretending to halt operations). All deleted
// (TASKS/analyst-escalation-view.md). A real kill-switch control belongs to
// a later, deliberate slice — a control that does nothing must not render.

function ErrorNote({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 rounded-xl border border-danger/30 bg-danger-light p-4 text-sm text-danger">
      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
      <span>{message} Nothing is shown because nothing could be loaded.</span>
    </div>
  )
}

export function AdminDashboard() {
  const tenantId = useAuthStore((s) => s.principal?.tenant_id ?? '')
  const [activeTab, setActiveTab] = useState<
    'overview' | 'principals' | 'tenants' | 'accounts' | 'canaries' | 'audit'
  >('overview')

  const [stats, setStats] = useState<AdminDashboardStats | null>(null)
  const [statsError, setStatsError] = useState<string | null>(null)
  const [principals, setPrincipals] = useState<Principal[]>([])
  const [principalsError, setPrincipalsError] = useState<string | null>(null)
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [tenantsError, setTenantsError] = useState<string | null>(null)
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([])
  const [auditError, setAuditError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    Promise.allSettled([
      getAdminDashboard().then(setStats),
      getPrincipals().then(setPrincipals),
      getTenants().then(setTenants),
      getAuditLog().then(setAuditLog),
    ]).then(([s, p, t, a]) => {
      if (s.status === 'rejected') setStatsError('Could not load dashboard stats.')
      if (p.status === 'rejected') setPrincipalsError('Could not load principals.')
      if (t.status === 'rejected') setTenantsError('Could not load tenants.')
      if (a.status === 'rejected') setAuditError('Could not load the audit log.')
      setIsLoading(false)
    })
  }, [])

  return (
    <div className="min-h-screen bg-cream">
      <AdminNavigation />

      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Tab navigation */}
        <div className="flex items-center gap-1 mb-6 border-b border-gray-200">
          {(
            [
              ['overview', 'Overview'],
              ['principals', 'Principals'],
              ['tenants', 'Tenants'],
              ['accounts', 'Accounts'],
              ['canaries', 'Canaries'],
              ['audit', 'Audit Log'],
            ] as const
          ).map(([tab, label]) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={cn(
                'tap-min px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                activeTab === tab
                  ? 'border-brass text-brass'
                  : 'border-transparent text-gray-500 hover:text-navy',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-brass border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Overview tab */}
        {!isLoading && activeTab === 'overview' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {statsError ? (
              <ErrorNote message={statsError} />
            ) : stats ? (
              <div className="grid grid-cols-4 gap-4">
                {[
                  { label: 'Active Cases', value: stats.active_cases, icon: Activity, color: 'text-brass', bg: 'bg-brass/10' },
                  { label: 'Alerts Today', value: stats.alerts_today, icon: AlertTriangle, color: 'text-warning', bg: 'bg-warning/10' },
                  { label: 'Tenants', value: stats.totals.tenants, icon: Building2, color: 'text-info', bg: 'bg-info/10' },
                  { label: 'Principals', value: stats.totals.principals, icon: Users, color: 'text-success', bg: 'bg-success/10' },
                ].map((stat, i) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="bg-white rounded-xl border border-gray-200 p-4"
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center', stat.bg)}>
                        <stat.icon className={cn('w-4 h-4', stat.color)} aria-hidden="true" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-navy">{stat.value}</p>
                        <p className="text-xs text-gray-500">{stat.label}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : null}

            {stats && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-sm text-gray-600 flex items-center gap-2">
                <Shield className="w-4 h-4 text-brass" aria-hidden="true" />
                Ingestion: {stats.ingestion.accounts_synced_last_hour} account
                {stats.ingestion.accounts_synced_last_hour === 1 ? '' : 's'} with a fresh
                look ({stats.ingestion.status}) · {stats.totals.monitored_accounts}{' '}
                accounts on file
              </div>
            )}
          </motion.div>
        )}

        {/* Principals tab */}
        {!isLoading && activeTab === 'principals' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {principalsError ? (
              <ErrorNote message={principalsError} />
            ) : principals.length === 0 ? (
              <EmptyState icon={Users} title="No principals" message="No principals are onboarded yet." />
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Email</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Role</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Autonomy Tier</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Channels</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {principals.map((principal) => (
                        <tr key={principal.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-navy">{principal.display_name}</td>
                          <td className="px-4 py-3 text-gray-600">{principal.email}</td>
                          <td className="px-4 py-3">
                            <span
                              className={cn(
                                'px-2 py-0.5 rounded-full text-xs font-medium capitalize',
                                principal.role === 'admin'
                                  ? 'bg-danger-light text-danger'
                                  : principal.role === 'analyst'
                                    ? 'bg-info-light text-info'
                                    : 'bg-success-light text-success',
                              )}
                            >
                              {principal.role}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            Tier {principal.autonomy_profile?.default_tier ?? '—'}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {principal.autonomy_profile?.channels?.join(', ') ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Tenants tab */}
        {!isLoading && activeTab === 'tenants' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {tenantsError ? (
              <ErrorNote message={tenantsError} />
            ) : tenants.length === 0 ? (
              <EmptyState icon={Building2} title="No tenants" message="No tenants are onboarded yet." />
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Slug</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                        <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {tenants.map((tenant) => (
                        <tr key={tenant.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-navy">{tenant.name}</td>
                          <td className="px-4 py-3 text-gray-500 font-mono text-xs">{tenant.slug}</td>
                          <td className="px-4 py-3">
                            <StatusBadge status={tenant.status} size="sm" />
                          </td>
                          <td className="px-4 py-3">
                            <TenantLifecycleControls
                              tenant={tenant}
                              onStatusChange={(id, status) =>
                                setTenants((prev) =>
                                  prev.map((t) =>
                                    t.id === id ? { ...t, status } : t,
                                  ),
                                )
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Accounts tab — list accounts on file (GET /accounts) and add a
            new one against the repaired POST /accounts create path. */}
        {!isLoading && activeTab === 'accounts' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <AccountsPanel tenantId={tenantId} />
          </motion.div>
        )}

        {/* Canaries tab — read-only list of deployed canary tokens and their
            fire counts (GET /canary/tokens). No secrets, no deploy path here. */}
        {!isLoading && activeTab === 'canaries' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <CanariesPanel />
          </motion.div>
        )}

        {/* Audit Log tab */}
        {!isLoading && activeTab === 'audit' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {auditError ? (
              <ErrorNote message={auditError} />
            ) : auditLog.length === 0 ? (
              <EmptyState
                icon={Eye}
                title="No audit entries"
                message="No response actions have been recorded (response is disarmed)."
              />
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Time</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Action</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Case</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {auditLog.map((entry) => (
                        <tr key={entry.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                            {entry.created_at ? timeAgo(entry.created_at) : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <code className="text-xs text-navy">{entry.action_type}</code>
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={entry.status} size="sm" />
                          </td>
                          <td className="px-4 py-3 text-gray-600 font-mono text-xs">
                            {entry.case_id ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </main>
    </div>
  )
}
